<script setup>
import { computed, reactive, ref, watch } from 'vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlDropdown from '../xl/XlDropdown.vue';
import XlStatusIcon from '../xl/XlStatusIcon.vue';
import { handOverReleasedBook, listReleasedBooks } from '../../api/policyholders';

const props = defineProps({
  // The directory the page already loaded; loading it is what releases a
  // departed broker's book, so the notice reads after it arrives.
  brokers: { type: Array, default: () => [] },
});

const emit = defineEmits(['settled']);

const released = ref([]);
const target = reactive({});
const busy = ref(null);

const choices = computed(() => props.brokers.map((b) => ({
  value: b.id,
  label: `${b.first_name ?? ''} ${b.last_name ?? ''}`.trim() || b.email,
})));

const load = async () => {
  try {
    released.value = await listReleasedBooks();
  } catch (e) {
    released.value = [];
  }
};

const settle = async (book, toBrokerId) => {
  busy.value = book.id;

  try {
    const result = await handOverReleasedBook(book.id, toBrokerId);
    window.loadNotification?.({
      message: toBrokerId === null
        ? 'Left unassigned'
        : `${result.moved.length} moved to ${choices.value.find((c) => c.value === toBrokerId)?.label ?? 'their new broker'}`,
    });
    await load();
    emit('settled');
  } catch (error) {
    window.loadNotification?.({
      message: error.response?.data?.message || 'The book could not be handed over.',
      error: true,
    });
  } finally {
    busy.value = null;
  }
};

watch(() => props.brokers, load, { immediate: props.brokers.length > 0 });
</script>

<template>
  <XlCard
    v-for="book in released"
    :key="book.id"
    padded
    tone="error"
    class="released-book"
    data-testid="released-book"
  >
    <XlStatusIcon tone="error">!</XlStatusIcon>
    <div class="released-book__text">
      <b>{{ book.name || 'A former broker' }} is no longer a broker here</b>
      <span>
        {{ book.policyholders }} {{ book.policyholders === 1 ? 'policyholder needs' : 'policyholders need' }} a broker.
      </span>
    </div>
    <XlDropdown
      v-model="target[book.id]"
      :options="choices"
      placeholder="Choose a broker"
      aria-label="Broker to take over this book"
      data-testid="released-book-broker"
    />
    <XlButton
      :disabled="!target[book.id]"
      :loading="busy === book.id"
      data-testid="released-book-move"
      @click="settle(book, target[book.id])"
    >
      Move them
    </XlButton>
    <XlButton variant="ghost" :disabled="busy === book.id" data-testid="released-book-leave" @click="settle(book, null)">
      Leave unassigned
    </XlButton>
  </XlCard>
</template>

<style scoped>
.released-book {
  display: flex;
  gap: 13px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.released-book__text {
  flex: 1;
  min-width: 200px;
  font-family: var(--xl-font-family);
}

.released-book__text b {
  display: block;
  font-size: var(--xl-type-title-small-size);
  color: var(--xl-color-primary-text);
}

.released-book__text span {
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-secondary-text);
}
</style>
